import React from 'react';

/**
 * The stabilimentum: the zigzag silk ribbon an orb-weaver spider signs its
 * web with — the one visible flourish in an otherwise invisible structure.
 * These components carry that signature through the interface.
 */

const patternId = (color: string, height: number) =>
    `st-zz-${height}-${color.replace(/[^a-zA-Z0-9]/g, '')}`;

/**
 * Full-width zigzag ribbon. Replaces the flat accent stripe on cards,
 * shells and modals; every surface carries the mark in its own dye.
 */
export const ZigzagStrip: React.FC<{ color: string; height?: number }> = ({ color, height = 7 }) => {
    const id = patternId(color, height);
    return (
        <div aria-hidden="true" style={{ height, backgroundColor: color + '16', lineHeight: 0 }}>
            <svg width="100%" height={height} style={{ display: 'block' }}>
                <defs>
                    <pattern id={id} width="12" height={height} patternUnits="userSpaceOnUse">
                        <path
                            d={`M0 ${height - 1.8} L3 1.8 L6 ${height - 1.8} L9 1.8 L12 ${height - 1.8}`}
                            fill="none"
                            stroke={color}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </pattern>
                </defs>
                <rect width="100%" height={height} fill={`url(#${id})`} />
            </svg>
        </div>
    );
};

/** Short standalone zigzag glyph — dividers, empty states, the brand mark. */
export const Zigzag: React.FC<{
    color: string;
    width?: number;
    height?: number;
    strokeWidth?: number;
    style?: React.CSSProperties;
}> = ({ color, width = 42, height = 9, strokeWidth = 1.8, style }) => {
    const peaks = Math.max(2, Math.round(width / 10));
    const step = width / (peaks * 2);
    const yTop = strokeWidth;
    const yBottom = height - strokeWidth;
    let d = `M0 ${yBottom}`;
    for (let i = 1; i <= peaks * 2; i++) {
        d += ` L${(i * step).toFixed(2)} ${i % 2 === 1 ? yTop : yBottom}`;
    }
    return (
        <svg aria-hidden="true" width={width} height={height} style={{ display: 'inline-block', flexShrink: 0, ...style }}>
            <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

/** Wordmark for dark chrome: zigzag glyph + tracked caps. */
export const BrandMark: React.FC<{
    fontSize?: string;
    color?: string;
    glyphColor?: string;
}> = ({ fontSize = '0.92rem', color = '#F4F4F5', glyphColor = '#8B77E8' }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
        <Zigzag color={glyphColor} width={24} height={8} strokeWidth={1.8} />
        <span style={{
            fontFamily: 'var(--st-font-display)',
            fontWeight: 700,
            fontSize,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color,
            lineHeight: 1,
        }}>
            Stabilimentum
        </span>
    </span>
);
